import { Appbar } from '@Components/app-bar';
import { Box, Button, styled } from '@mui/material';
import AddNewIcon from '../../assets/icons/add-new-user-icon.svg';
import useTableRequestParams from 'hooks/useTableRequestParams';
import { appVersionsApi  } from '@Store/api/appVersionsSlice';
import { IAppVersion } from '@Types/AppVersionType';
import { versionColumns } from './columns';
import Table from '@Components/table';
import useActionColumn from '@Components/table/useActionColumn';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useModal from 'hooks/useModal';
import { Modal } from '@Components/modal';
import { DeleteAppVersionContainer } from './DeleteAppVersionContainer';
import { IPolygonFormData } from 'pages/space-tarrif-map-new-customer/CustomerForm.validation';
import { handleFetchError } from '@Utils/handleError';
import { CreateAppVersionForm } from './create-app-version/CreateAppVersionForm.container';
import { useMemo } from 'react';

const Container = styled(Box)`
  padding: 0px;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const AddNewVersionButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  padding: '18px 20px',
  borderRadius: 30,
  color: '#FFF',
  img: {
    marginRight: 10,
  },
  textTransform: 'capitalize',
  '&:hover': { backgroundColor: theme.palette.secondary.main },
}));

const { useGetAllAppVersionsQuery, useRemoveAppVersionMutation } = appVersionsApi;


export function AppControlVersions() {
  const {
    isOpen: isDeleteModalOpen,
    item: versionToDelete,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal<IAppVersion>();

  const {
    isOpen: isAddVersionModalOpen,
    openModal: openAddVersionModal,
    closeModal: closeAddVersionModal,
  } = useModal<IPolygonFormData>();

  const [searchParams] = useSearchParams();

  const { createdAtFrom, createdAtTo } = useMemo(() => {
    const now = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(now.getFullYear() - 1);

    return {
      createdAtFrom: oneYearAgo.toISOString(),
      createdAtTo: now.toISOString(),
    };
  }, []);
  
  const tableParams = useTableRequestParams();
  const { currentPage, setCurrentPage } = tableParams;
  const queryArgs = useMemo(() => {
    return {
      currentPage: tableParams.currentPage,
      pageSize: tableParams.pageSize,
      search: tableParams.search,
      createdAtFrom: '2000-01-01T00:00:00.000Z',
      createdAtTo: '2100-01-01T00:00:00.000Z',
      getAllPages: false,
      sortFields: tableParams.sortFields ?? [],
    };
  }, [
    tableParams.currentPage,
    tableParams.pageSize,
    tableParams.search,
    tableParams.sortFields,
    createdAtFrom,
    createdAtTo,
  ]);
  const { data: appVersions, error: tableDataError, refetch } = useGetAllAppVersionsQuery(queryArgs, {
     refetchOnMountOrArgChange: true,
  });

  const [deleteVersion] = useRemoveAppVersionMutation();

  const handleOpenDeleteVersion = useCallback(
    (version: IAppVersion) => {
      openDeleteModal(version);
    },
    [openDeleteModal]
  );

  const handleDelete = async () => {
    await deleteVersion(versionToDelete.id!).unwrap();
    refetch(); 
    closeDeleteModal();
  };

  const actionColumn = useActionColumn({
    handleDelete: handleOpenDeleteVersion,
  });

  useEffect(() => {
    handleFetchError(tableDataError);
  }, [tableDataError]);

  useEffect(() => {
    if (appVersions?.items?.length === 0 && currentPage > 1) {
      setCurrentPage(currentPage - 1); 
    }
  }, [appVersions?.items, currentPage]);

  useEffect(() => {
    if (searchParams.get('Page.PageNumber') === '1') {
      refetch(); 
    }
  }, [searchParams, refetch]);

  return (
    <Container>
      <Appbar searchable title={'App Control Versions'}>
        <AddNewVersionButton
          onClick={openAddVersionModal}
        >
          <img alt='Add App Version' src={AddNewIcon} />
          Add App Version
        </AddNewVersionButton>
      </Appbar>
      <Table
        searchable
        rows={appVersions?.items || []}
        columns={[...versionColumns, actionColumn]}
        totalCount={appVersions?.count || 0}
        paginated={
          appVersions?.items.length
            ? appVersions.items.length > 0
            : false
        }
      />

      {isDeleteModalOpen ? (
        <Modal
          labelErrored
          handleClose={closeDeleteModal}
          label={'Delete App Version'}
          open={isDeleteModalOpen}
        >
          <DeleteAppVersionContainer
            version={versionToDelete}
            onDelete={handleDelete}
          />
        </Modal>
      ) : null}
      {isAddVersionModalOpen ? (
        <Modal
          handleClose={closeAddVersionModal}
          label={'Add Version'}
          open={isAddVersionModalOpen}
        >
          <CreateAppVersionForm
            handleClose={closeAddVersionModal}
            onSuccess={() => {
              tableParams.setCurrentPage(1);
            }}
          />
        </Modal>
      ) : null}
    </Container>
  );
}